/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo, useContext } from "react"
import { sortByProp } from "../helper/arrays"
import PageContext from "../components/page-context"
import { t } from "../i18n-utility"

const FILTER_TYPE_CATEGORY = "category"
const FILTER_TYPE_FORMAT = "format"

const getCardFormat = (cardElement) => {
  return cardElement.content.card_format_override
    ? cardElement.content.card_format_override
    : cardElement.type
}

const useLocalCollectionHandler = (
  collection,
  items_per_page,
  filterType = "category_format",
  startWithPage,
  optionalCategoryWhiteList = ""
) => {
  const { translations } = useContext(PageContext)

  const itemsPerPage = items_per_page
  const [maxPages, setMaxPages] = useState(
    Math.ceil(collection.length / itemsPerPage)
  )
  const [fullCollection] = useState(collection)
  const [filteredFullCollection, setFilteredFullCollection] =
    useState(collection)
  const [totalCount, setTotalCount] = useState(collection.length)
  const [collectionForPage, setCollectionForPage] = useState([])
  const [availableFilterOptions, setAvailableFilterOptions] = useState({})
  const [activePage, setActivePage] = useState(startWithPage || 1)
  const [hasNextPage, setHasNextPage] = useState(false)
  const categoryWhitelist =
    optionalCategoryWhiteList !== "" && optionalCategoryWhiteList !== null
      ? optionalCategoryWhiteList.split(",").map((e) => {
          return parseInt(e)
        })
      : []

  const filterRootCategories = useMemo(
    () =>
      sortByProp(
        "title",
        collection.reduce((outerResult, item) => {
          return (item.content.root_categories || []).reduce(
            (result, category) => {
              // we skip the category if it is the "DE" root (which has only two letters in the title)
              if (
                category.title &&
                category.title.length > 2 &&
                !result.find((aCategory) => category.uid === aCategory.uid)
              ) {
                if (
                  process.env.LIMIT_COLLECTIONS_FILTER_CATEGORIES === "true" &&
                  categoryWhitelist.length > 0
                ) {
                  if (categoryWhitelist.indexOf(category.uid) > -1) {
                    result.push(category)
                  }
                } else {
                  result.push(category)
                }
              }
              return result
            },
            outerResult
          )
        }, [])
      ),
    [collection]
  )

  const filterFormat = useMemo(
    () =>
      sortByProp(
        "title",
        collection.reduce((result, item) => {
          const format = getCardFormat(item)
          if (!result.find((aFormat) => format === aFormat.uid)) {
            const title = t(translations, `filters.${format}`, true) || format
            result.push({
              uid: format,
              title,
            })
          }
          return result
        }, [])
      ),
    [collection]
  )

  const sliceCollectionForPage = (page) => {
    const itemLimit = page * itemsPerPage
    setCollectionForPage(filteredFullCollection.slice(0, itemLimit))
    setHasNextPage(page < maxPages)
    setActivePage(page)
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      sliceCollectionForPage(activePage + 1)
    }
  }

  const filterCollection = (collection, filter) => {
    let filteredCollection = collection
    let options = filter
    if (options.formats.length) {
      filteredCollection = filteredCollection.reduce((result, item) => {
        const format = getCardFormat(item)
        if (options.formats.includes(format)) {
          result.push(item)
        }
        return result
      }, [])
    }
    if (options.root_categories.length) {
      filteredCollection = filteredCollection.reduce((result, item) => {
        if (
          item.content?.root_categories?.find((category) =>
            options.root_categories.includes(category.uid + "")
          )
        ) {
          result.push(item)
        }
        return result
      }, [])
    }
    return filteredCollection
  }

  const setSelectedFilterOptions = (options) => {
    let filteredCollection = filterCollection(fullCollection, options)
    let filterOptionsCopy = availableFilterOptions
    if (
      options.formats &&
      options.formats.length > 0 &&
      filterOptionsCopy.root_categories
    ) {
      filterOptionsCopy.root_categories.map((c) => {
        if (
          filterCollection(filteredCollection, {
            root_categories: ["" + c.uid],
            formats: [],
          }).length > 0
        ) {
          c.disabled = false
        } else {
          c.disabled = true
        }
        return c
      })
    } else if (
      options.root_categories.length > 0 &&
      filterOptionsCopy.formats
    ) {
      filterOptionsCopy.formats.map((c) => {
        if (
          filterCollection(filteredCollection, {
            root_categories: [],
            formats: [c.uid],
          }).length > 0
        ) {
          c.disabled = false
        } else {
          c.disabled = true
        }
        return c
      })
    } else {
      if (filterOptionsCopy.formats) {
        filterOptionsCopy.formats.map((c) => {
          c.disabled = false
          return c
        })
      }
      if (filterOptionsCopy.root_categories) {
        filterOptionsCopy.root_categories.map((c) => {
          c.disabled = false
          return c
        })
      }
    }

    setTotalCount(filteredCollection.length)
    setFilteredFullCollection(filteredCollection)
  }

  useEffect(() => {
    // reset activePage to first, when collection was filtered
    sliceCollectionForPage(1)
    setMaxPages(Math.ceil(filteredFullCollection.length / itemsPerPage))
  }, [filteredFullCollection])

  useEffect(() => {
    setHasNextPage(activePage < maxPages)
  }, [maxPages])

  // init
  useEffect(() => {
    if (collection.length > 0) {
      let filterOptions = {}
      if (filterType?.includes(FILTER_TYPE_FORMAT) && filterFormat.length > 0) {
        filterOptions["formats"] = filterFormat
      }
      if (
        filterType?.includes(FILTER_TYPE_CATEGORY) &&
        filterRootCategories.length > 0
      ) {
        filterOptions["root_categories"] = filterRootCategories
      }
      setAvailableFilterOptions(filterOptions)
      sliceCollectionForPage(1)
    }
  }, [])

  return {
    collectionForPage,
    activePage,
    hasNextPage,
    totalCount,
    availableFilterOptions,
    setFilteredFullCollection,
    setSelectedFilterOptions,
    handleNextPage,
  }
}

export default useLocalCollectionHandler
